/**
 * @module features/client-device-management/components/DeviceConfigDrawer
 * @description Dynamic parameter catalog & custom configuration drawer (mobile bottom sheet /
 * desktop right slide-over). Auto-renders validated form controls for `string`,
 * `number`, `boolean`, `enum`, and `json` parameter definitions, supports custom
 * key-value parameter additions and operational notes editing, and scales across
 * 10+ IoT gateway categories without UI code changes.
 */
import * as React from "react";
import {
  AlertCircle,
  CheckCircle2,
  Loader2,
  Plus,
  SlidersHorizontal,
  Trash2,
} from "lucide-react";
import { Drawer } from "@/components/ui/drawer";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { AppError } from "@/lib/api-client";
import {
  AssignmentSummary,
  DeviceConfigParameter,
  formatDeviceTypeLabel,
} from "../types";
import { useAssignmentConfigs } from "../api/configApi";
import { useUpdateAssignment } from "../api/assignmentApi";

export interface DeviceConfigDrawerProps {
  assignment: AssignmentSummary | null;
  open: boolean;
  onClose: () => void;
  onSaved?: (updated: AssignmentSummary) => void;
  deviceTypes?: ReadonlyArray<{ type_id: string; label: string }>;
}

interface CustomParamDraft {
  id: string;
  key: string;
  value: string;
}

const CUSTOM_KEY_PATTERN = /^[a-zA-Z0-9_.-]{1,64}$/;

/**
 * Validates a single parameter value against its declarative catalog rules.
 */
function validateCatalogValue(
  param: DeviceConfigParameter,
  rawValue: string
): string | null {
  const value = rawValue.trim();
  const rules = param.validation_rule ?? {};

  if (param.is_required && !value) {
    return `${param.label} is required.`;
  }
  if (!value) return null;

  if (param.data_type === "number") {
    const parsed = Number(value);
    if (Number.isNaN(parsed)) {
      return `${param.label} must be a valid number.`;
    }
    if (typeof rules.min === "number" && parsed < rules.min) {
      return `${param.label} must be at least ${rules.min}.`;
    }
    if (typeof rules.max === "number" && parsed > rules.max) {
      return `${param.label} cannot exceed ${rules.max}.`;
    }
    return null;
  }

  if (param.data_type === "boolean") {
    if (!["true", "false", "1", "0"].includes(value.toLowerCase())) {
      return `${param.label} must be true or false.`;
    }
    return null;
  }

  if (param.data_type === "enum") {
    const allowed = rules.allowed_values ?? [];
    if (allowed.length > 0 && !allowed.includes(value)) {
      return `${param.label} must be one of: ${allowed.join(", ")}.`;
    }
    return null;
  }

  if (param.data_type === "json") {
    try {
      JSON.parse(value);
      return null;
    } catch {
      return `${param.label} must be valid JSON.`;
    }
  }

  // String validation
  if (typeof rules.min_length === "number" && value.length < rules.min_length) {
    return `${param.label} must be at least ${rules.min_length} characters.`;
  }
  if (typeof rules.max_length === "number" && value.length > rules.max_length) {
    return `${param.label} cannot exceed ${rules.max_length} characters.`;
  }
  if (typeof rules.regex === "string" && rules.regex) {
    try {
      const pattern = new RegExp(rules.regex);
      if (!pattern.test(value)) {
        return `${param.label} format is invalid.`;
      }
    } catch {
      // Ignore malformed server regex on client; backend enforces authoritative check.
    }
  }
  return null;
}

export function DeviceConfigDrawer({
  assignment,
  open,
  onClose,
  onSaved,
  deviceTypes,
}: DeviceConfigDrawerProps) {
  const configsQuery = useAssignmentConfigs(assignment?.id, open);
  const updateAssignmentMutation = useUpdateAssignment();

  const [values, setValues] = React.useState<Record<string, string>>({});
  const [notes, setNotes] = React.useState("");
  const [customDrafts, setCustomDrafts] = React.useState<CustomParamDraft[]>(
    []
  );
  const [fieldErrors, setFieldErrors] = React.useState<Record<string, string>>(
    {}
  );
  const [submitError, setSubmitError] = React.useState<AppError | null>(null);
  const [savedNotice, setSavedNotice] = React.useState(false);

  const assignmentId = assignment?.id;

  // Reset transient notices/errors only when opening the drawer or switching assignments
  React.useEffect(() => {
    if (!assignmentId || !open) return;
    setNotes(assignment?.notes ?? "");
    setCustomDrafts([]);
    setFieldErrors({});
    setSubmitError(null);
    setSavedNotice(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [assignmentId, open]);

  // Hydrate parameter values whenever fetched parameters arrive or refresh
  React.useEffect(() => {
    if (!assignment || !open) return;
    const initial: Record<string, string> = {};
    if (configsQuery.data && configsQuery.data.length > 0) {
      configsQuery.data.forEach((param) => {
        if (
          param.config_key === "custom_device_name" &&
          !param.config_value &&
          assignment.custom_device_name
        ) {
          initial[param.config_key] = assignment.custom_device_name;
        } else if (
          param.config_key === "location" &&
          !param.config_value &&
          assignment.location
        ) {
          initial[param.config_key] = assignment.location;
        } else {
          initial[param.config_key] =
            param.config_value ?? param.default_value ?? "";
        }
      });
    } else {
      initial.custom_device_name = assignment.custom_device_name ?? "";
      initial.location = assignment.location ?? "";
    }
    setValues(initial);
  }, [configsQuery.data, assignment, open]);

  const handleClose = (): void => {
    setFieldErrors({});
    setCustomDrafts([]);
    setSubmitError(null);
    setSavedNotice(false);
    onClose();
  };

  const isEditable = assignment?.status === "active";

  // Ensure built-in editable base parameters are always surfaced even if catalog API returned []
  const parameters: DeviceConfigParameter[] = React.useMemo(() => {
    const raw = configsQuery.data ?? [];
    if (raw.length > 0) return raw;
    if (!assignment) return [];
    return [
      {
        config_key: "custom_device_name",
        label: "Device Friendly Name",
        description:
          "Optional friendly alias displayed in place of the hardware name.",
        data_type: "string",
        config_value: assignment.custom_device_name ?? "",
        default_value: "",
        is_required: false,
        validation_rule: { max_length: 150 },
        display_order: 1,
      },
      {
        config_key: "location",
        label: "Deployment Location",
        description:
          "Physical placement location (e.g. Building A, Floor 2 Reception).",
        data_type: "string",
        config_value: assignment.location ?? "",
        default_value: "",
        is_required: false,
        validation_rule: { max_length: 255 },
        display_order: 2,
      },
    ];
  }, [configsQuery.data, assignment]);

  const handleFieldChange = (key: string, nextVal: string): void => {
    setValues((prev) => ({ ...prev, [key]: nextVal }));
    setSavedNotice(false);
    if (fieldErrors[key]) {
      setFieldErrors((prev) => {
        const copy = { ...prev };
        delete copy[key];
        return copy;
      });
    }
  };

  const handleAddCustomDraft = (): void => {
    setCustomDrafts((prev) => [
      ...prev,
      {
        id: `draft-${Date.now()}-${prev.length}`,
        key: "",
        value: "",
      },
    ]);
    setSavedNotice(false);
  };

  const handleUpdateCustomDraft = (
    draftId: string,
    patch: Partial<Pick<CustomParamDraft, "key" | "value">>
  ): void => {
    setCustomDrafts((prev) =>
      prev.map((d) => (d.id === draftId ? { ...d, ...patch } : d))
    );
    setSavedNotice(false);
    if (fieldErrors[draftId]) {
      setFieldErrors((prev) => {
        const copy = { ...prev };
        delete copy[draftId];
        return copy;
      });
    }
  };

  const handleRemoveCustomDraft = (draftId: string): void => {
    setCustomDrafts((prev) => prev.filter((d) => d.id !== draftId));
    setFieldErrors((prev) => {
      const copy = { ...prev };
      delete copy[draftId];
      return copy;
    });
  };

  const handleSubmit = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();
    if (!assignment || !isEditable) return;

    const nextErrors: Record<string, string> = {};
    parameters.forEach((param) => {
      const err = validateCatalogValue(param, values[param.config_key] ?? "");
      if (err) nextErrors[param.config_key] = err;
    });

    const mergedConfigs: Record<string, string> = { ...values };
    customDrafts.forEach((draft) => {
      const trimmedKey = draft.key.trim();
      const trimmedVal = draft.value;
      if (!trimmedKey && !trimmedVal.trim()) {
        return;
      }
      if (!CUSTOM_KEY_PATTERN.test(trimmedKey)) {
        nextErrors[draft.id] =
          "Key must be 1–64 letters, numbers, underscores, dots, or hyphens.";
        return;
      }
      mergedConfigs[trimmedKey] = trimmedVal;
    });

    if (notes.length > 2000) {
      nextErrors.__notes = "Operational notes cannot exceed 2,000 characters.";
    }

    if (Object.keys(nextErrors).length > 0) {
      setFieldErrors(nextErrors);
      return;
    }

    setSubmitError(null);
    setSavedNotice(false);
    try {
      const updated = await updateAssignmentMutation.mutateAsync({
        assignmentId: assignment.id,
        input: {
          notes: notes.trim(),
          configs: mergedConfigs,
        },
      });
      setCustomDrafts([]);
      setSavedNotice(true);
      onSaved?.(updated);
    } catch (rawErr: unknown) {
      setSubmitError(
        AppError.fromUnknown(rawErr, "Failed to save device configuration.")
      );
    }
  };

  const deviceTitle = assignment
    ? assignment.custom_device_name || assignment.device_name
    : "Device Configuration";
  const gatewayTypeLabel = assignment
    ? formatDeviceTypeLabel(assignment.device_type, deviceTypes)
    : "";

  return (
    <Drawer
      open={open}
      onClose={handleClose}
      title={`${deviceTitle} — Configuration`}
      description={
        assignment
          ? `${gatewayTypeLabel} · ${assignment.registered_device_id}`
          : undefined
      }
    >
      {configsQuery.isLoading ? (
        <div className="space-y-4" aria-busy="true">
          <Skeleton className="h-16 w-full rounded-xl" />
          <Skeleton className="h-16 w-full rounded-xl" />
          <Skeleton className="h-10 w-32 rounded-md ml-auto" />
        </div>
      ) : configsQuery.isError ? (
        <div
          role="alert"
          className="rounded-xl border border-danger/20 bg-danger-soft p-4 text-xs text-danger space-y-2"
        >
          <p>{AppError.fromUnknown(configsQuery.error).errorDetails}</p>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => void configsQuery.refetch()}
            className="bg-card text-foreground text-xs"
          >
            Retry
          </Button>
        </div>
      ) : !isEditable && parameters.length === 0 ? (
        <div className="rounded-xl border border-dashed border-border p-8 text-center">
          <SlidersHorizontal
            className="mx-auto h-6 w-6 text-muted-foreground"
            aria-hidden="true"
          />
          <p className="mt-2 text-sm font-medium text-foreground">
            No historical parameters stored for {gatewayTypeLabel}
          </p>
        </div>
      ) : (
        <form onSubmit={handleSubmit} className="space-y-5" noValidate>
          {!isEditable && (
            <div className="rounded-xl border border-warning/30 bg-warning-soft p-3 text-xs text-warning">
              This assignment is {assignment?.status}; configuration values are
              read-only for historical inspection.
            </div>
          )}

          {submitError && (
            <div
              role="alert"
              className="flex items-start gap-2 rounded-xl border border-danger/20 bg-danger-soft p-3 text-xs text-danger"
            >
              <AlertCircle
                className="h-4 w-4 shrink-0 mt-0.5"
                aria-hidden="true"
              />
              <div className="min-w-0">
                <p>{submitError.errorDetails}</p>
                {submitError.requestId && (
                  <p className="mt-1 font-mono text-[11px] text-muted-foreground break-all">
                    Request ID: {submitError.requestId}
                  </p>
                )}
              </div>
            </div>
          )}

          {savedNotice && (
            <div
              role="status"
              className="flex items-center gap-2 rounded-xl border border-success/20 bg-success-soft p-3 text-xs text-success"
            >
              <CheckCircle2 className="h-4 w-4 shrink-0" aria-hidden="true" />
              <span>Device configurations updated.</span>
            </div>
          )}

          {/* Catalog & Stored Configuration Parameters */}
          <fieldset disabled={!isEditable} className="space-y-4">
            <legend className="text-xs font-semibold text-foreground">
              Device Parameters
            </legend>

            {parameters.map((param) => {
              const inputId = `cfg-${param.config_key}`;
              const currentVal = values[param.config_key] ?? "";
              const fieldError = fieldErrors[param.config_key];
              const enumChoices = param.validation_rule?.allowed_values ?? [];

              return (
                <div key={param.config_key} className="space-y-1.5">
                  <div className="flex items-center justify-between gap-2">
                    <label
                      htmlFor={inputId}
                      className="text-xs font-medium text-foreground"
                    >
                      {param.label}
                      {param.is_required && (
                        <span className="text-danger ml-0.5">*</span>
                      )}
                    </label>
                    <span className="font-mono text-[10px] text-muted-foreground">
                      {param.config_key}
                    </span>
                  </div>

                  {param.data_type === "boolean" ? (
                    <div className="flex items-center gap-2.5 rounded-xl border border-border/80 bg-muted/20 px-3 py-2.5">
                      <Checkbox
                        id={inputId}
                        disabled={!isEditable}
                        checked={
                          currentVal.toLowerCase() === "true" ||
                          currentVal === "1"
                        }
                        onCheckedChange={(checked) =>
                          handleFieldChange(
                            param.config_key,
                            checked ? "true" : "false"
                          )
                        }
                      />
                      <label
                        htmlFor={inputId}
                        className="text-xs text-foreground cursor-pointer select-none"
                      >
                        {param.description || `Enable ${param.label}`}
                      </label>
                    </div>
                  ) : param.data_type === "enum" && enumChoices.length > 0 ? (
                    <Select
                      disabled={!isEditable}
                      value={currentVal}
                      onValueChange={(val) =>
                        handleFieldChange(param.config_key, val)
                      }
                    >
                      <SelectTrigger
                        id={inputId}
                        className="h-11 sm:h-10 bg-card rounded-xl"
                      >
                        <SelectValue placeholder={`Select ${param.label}`} />
                      </SelectTrigger>
                      <SelectContent>
                        {enumChoices.map((choice) => (
                          <SelectItem key={choice} value={choice}>
                            {choice}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  ) : param.data_type === "json" ? (
                    <textarea
                      id={inputId}
                      rows={4}
                      disabled={!isEditable}
                      value={currentVal}
                      onChange={(e) =>
                        handleFieldChange(param.config_key, e.target.value)
                      }
                      autoCorrect="off"
                      autoCapitalize="off"
                      spellCheck={false}
                      className="w-full rounded-xl border border-input bg-card p-3 font-mono text-base sm:text-xs text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-50"
                    />
                  ) : (
                    <Input
                      id={inputId}
                      type={param.data_type === "number" ? "number" : "text"}
                      inputMode={
                        param.data_type === "number" ? "decimal" : "text"
                      }
                      disabled={!isEditable}
                      value={currentVal}
                      maxLength={param.validation_rule?.max_length}
                      onChange={(e) =>
                        handleFieldChange(param.config_key, e.target.value)
                      }
                      className="h-11 sm:h-10 text-base sm:text-sm bg-card"
                    />
                  )}

                  {param.description && param.data_type !== "boolean" && (
                    <p className="text-[11px] text-muted-foreground">
                      {param.description}
                    </p>
                  )}

                  {fieldError && (
                    <p role="alert" className="text-[11px] text-danger">
                      {fieldError}
                    </p>
                  )}
                </div>
              );
            })}
          </fieldset>

          {/* Custom Key-Value Parameters */}
          {isEditable && (
            <fieldset className="space-y-3 rounded-xl border border-border/80 bg-muted/15 p-3.5">
              <div className="flex items-center justify-between gap-2">
                <legend className="text-xs font-semibold text-foreground">
                  Custom Key-Value Overrides
                </legend>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={handleAddCustomDraft}
                  className="h-8 touch:min-h-[44px] px-2.5 text-xs gap-1 bg-card"
                >
                  <Plus className="h-3.5 w-3.5" aria-hidden="true" />
                  <span>Add Parameter</span>
                </Button>
              </div>

              {customDrafts.length === 0 ? (
                <p className="text-[11px] text-muted-foreground">
                  Add custom runtime keys (e.g.{" "}
                  <span className="font-mono">refresh_interval_sec</span> or{" "}
                  <span className="font-mono">brightness_pct</span>) specific to
                  this hardware unit.
                </p>
              ) : (
                <div className="space-y-2.5">
                  {customDrafts.map((draft, idx) => (
                    <div key={draft.id} className="space-y-1">
                      <div className="grid grid-cols-1 sm:grid-cols-12 gap-2 items-center">
                        <div className="sm:col-span-5">
                          <Input
                            aria-label={`Custom parameter key ${idx + 1}`}
                            placeholder="parameter_key"
                            autoComplete="off"
                            autoCorrect="off"
                            autoCapitalize="off"
                            spellCheck={false}
                            value={draft.key}
                            onChange={(e) =>
                              handleUpdateCustomDraft(draft.id, {
                                key: e.target.value,
                              })
                            }
                            className="h-10 text-base sm:text-xs font-mono bg-card"
                          />
                        </div>
                        <div className="sm:col-span-6">
                          <Input
                            aria-label={`Custom parameter value ${idx + 1}`}
                            placeholder="Value"
                            autoComplete="off"
                            value={draft.value}
                            onChange={(e) =>
                              handleUpdateCustomDraft(draft.id, {
                                value: e.target.value,
                              })
                            }
                            className="h-10 text-base sm:text-xs bg-card"
                          />
                        </div>
                        <div className="sm:col-span-1 flex justify-end">
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            onClick={() => handleRemoveCustomDraft(draft.id)}
                            aria-label={`Remove custom parameter ${idx + 1}`}
                            className="h-8 w-8 touch:min-h-[44px] touch:min-w-[44px] text-muted-foreground hover:text-danger hover:bg-danger-soft"
                          >
                            <Trash2
                              className="h-3.5 w-3.5"
                              aria-hidden="true"
                            />
                          </Button>
                        </div>
                      </div>
                      {fieldErrors[draft.id] && (
                        <p role="alert" className="text-[11px] text-danger">
                          {fieldErrors[draft.id]}
                        </p>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </fieldset>
          )}

          {/* Operational Notes */}
          <fieldset disabled={!isEditable} className="space-y-1.5">
            <legend className="sr-only">Operational Notes</legend>
            <label
              htmlFor="cfg-operational-notes"
              className="text-xs font-medium text-foreground block"
            >
              Operational Notes
            </label>
            <textarea
              id="cfg-operational-notes"
              rows={3}
              maxLength={2000}
              disabled={!isEditable}
              value={notes}
              onChange={(e) => {
                setNotes(e.target.value);
                setSavedNotice(false);
              }}
              placeholder="Deployment instructions, mounting details, or contact remarks..."
              className="w-full rounded-xl border border-input bg-card p-3 text-base sm:text-xs text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-50"
            />
            {fieldErrors.__notes && (
              <p role="alert" className="text-[11px] text-danger">
                {fieldErrors.__notes}
              </p>
            )}
          </fieldset>

          <div className="pt-3 border-t border-border flex items-center justify-end gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={handleClose}
              className="h-10 sm:h-9 touch:min-h-[44px] px-4 text-xs"
            >
              Close
            </Button>
            {isEditable && (
              <Button
                type="submit"
                disabled={updateAssignmentMutation.isPending}
                className="h-10 sm:h-9 touch:min-h-[44px] px-4 text-xs min-w-[130px]"
              >
                {updateAssignmentMutation.isPending ? (
                  <>
                    <Loader2
                      className="mr-2 h-4 w-4 animate-spin"
                      aria-hidden="true"
                    />
                    <span>Saving...</span>
                  </>
                ) : (
                  <span>Save Configuration</span>
                )}
              </Button>
            )}
          </div>
        </form>
      )}
    </Drawer>
  );
}
